import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PORTAL_NAME, SHIFT } from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import DenominationGrid from '../components/DenominationGrid';
import { useDeviceErrorRedirect } from '../hooks/useDeviceErrorRedirect';
import { cashierCreateCashDrop, cashierGetShift } from '../services/cashierApi';
import { emptyDenominationGrid } from '../utils/denominations';
import { canJoinShift } from '../utils/shiftAccess';

export default function CashDropPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const handleDeviceError = useDeviceErrorRedirect();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [denominations, setDenominations] = useState(emptyDenominationGrid());

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await cashierGetShift(id);
        if (!res.success || !res.data?.shift) {
          toast.error(res.message || 'Shift not found');
          navigate('/', { replace: true });
          return;
        }
        if (!canJoinShift(res.data.shift.status)) {
          navigate(`/shifts/${id}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id, navigate]);

  if (!id) return <Navigate to="/" replace />;

  const onSubmit = async () => {
    const items = denominations.filter((d) => d.quantity > 0);
    if (items.length === 0) {
      toast.error('Enter at least one denomination');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cashierCreateCashDrop({
        shiftId: id,
        denominations: items,
      });
      if (!res.success) {
        if (handleDeviceError(res, `/shifts/${id}`)) return;
        toast.error(res.message || 'Cash drop failed');
        return;
      }
      toast.success('Cash drop recorded');
      navigate(`/shifts/${id}`, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CashierLayout
      title={SHIFT.cashDrop}
      subtitle="Transfer cash from the drawer to your manager or the safe"
      backTo={`/shifts/${id}`}
    >
      {loading ? (
        <div className="text-sm text-stone-600">Loading…</div>
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
          <p className="text-sm text-stone-600">
            Count the cash you are removing from the drawer. Your manager will confirm receipt in the {PORTAL_NAME.shop}.
          </p>
          <div className="mt-4">
            <DenominationGrid value={denominations} onChange={setDenominations} disabled={submitting} />
          </div>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting}
            className="mt-6 w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Submit cash drop'}
          </button>
        </div>
      )}
    </CashierLayout>
  );
}
